import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight, Globe, Monitor } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { unitLabel } from '../../lib/permissions';

const CATEGORY_STYLES = {
    'Sign-in & security': 'bg-slate-100 text-slate-700',
    'Users & roles': 'bg-indigo-50 text-indigo-700',
    Clients: 'bg-blue-50 text-blue-700',
    KYC: 'bg-emerald-50 text-emerald-700',
    'Assets & documents': 'bg-amber-50 text-amber-700',
    Setup: 'bg-purple-50 text-purple-700',
    'Data quality': 'bg-rose-50 text-rose-700',
};

export const categoryClass = (category) => CATEGORY_STYLES[category] || 'bg-gray-100 text-gray-700';

const pad = (n) => String(n).padStart(2, '0');

export function formatTime(iso) {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return { date: iso, time: '', relative: '' };
    const date = d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
    const time = `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    const secs = Math.round((Date.now() - d.getTime()) / 1000);
    let relative = '';
    if (secs < 60) relative = 'just now';
    else if (secs < 3600) relative = `${Math.floor(secs / 60)} min ago`;
    else if (secs < 86400) relative = `${Math.floor(secs / 3600)} h ago`;
    else if (secs < 86400 * 30) relative = `${Math.floor(secs / 86400)} d ago`;
    return { date, time, relative };
}

const humanKey = (k) => k.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());

const isPermission = (v) => typeof v === 'string' && /^[A-Z_]+:[A-Z_]+$/.test(v);

const valueText = (v) => {
    if (v === null || v === undefined || v === '') return '—';
    if (typeof v === 'boolean') return v ? 'Yes' : 'No';
    if (Array.isArray(v)) return v.length ? v.join(', ') : '—';
    if (typeof v === 'object') return JSON.stringify(v);
    return String(v);
};

export function detailSummary(data) {
    if (data === null || data === undefined) return '';
    if (typeof data !== 'object') return String(data);
    const added = Array.isArray(data.added) ? data.added.length : null;
    const removed = Array.isArray(data.removed) ? data.removed.length : null;
    if (added !== null || removed !== null) {
        const parts = [];
        if (added !== null) parts.push(`+${added}`);
        if (removed !== null) parts.push(`−${removed}`);
        return parts.join(' / ');
    }
    return Object.entries(data).slice(0, 2).map(([k, v]) => `${humanKey(k)}: ${valueText(v)}`).join(' · ');
}

export const recordText = (row) => row.entityLabel || (row.entityType ? `${row.entityType}${row.entityId ? ` #${row.entityId}` : ''}` : '');

function Chips({ items, catalog, tone }) {
    if (!items.length) return <span className="text-gray-400">—</span>;
    return (
        <div className="flex flex-wrap gap-1.5">
            {items.map((item, i) => (
                <span
                    key={`${item}-${i}`}
                    title={isPermission(item) ? item : undefined}
                    className={`rounded-full px-2 py-0.5 text-xs ${tone || 'bg-gray-100 text-gray-700'}`}
                >
                    {isPermission(item) && catalog ? unitLabel(catalog, item) : valueText(item)}
                </span>
            ))}
        </div>
    );
}

function Details({ row, catalog }) {
    const data = row.data;
    const entries = data && typeof data === 'object' && !Array.isArray(data) ? Object.entries(data) : [];
    return (
        <div className="grid gap-6 px-6 py-4 md:grid-cols-[minmax(0,1fr)_280px]">
            <div>
                <h4 className="mb-2 text-xs font-medium uppercase tracking-wider text-gray-500">Details</h4>
                {data === null || data === undefined ? (
                    <p className="text-sm text-gray-400">No extra details were recorded for this event.</p>
                ) : entries.length ? (
                    <dl className="divide-y divide-gray-100 rounded-lg border border-gray-200 bg-white">
                        {entries.map(([k, v]) => (
                            <div key={k} className="grid grid-cols-[160px_minmax(0,1fr)] gap-3 px-3 py-2 text-sm">
                                <dt className="text-gray-500">{humanKey(k)}</dt>
                                <dd className="min-w-0 break-words text-gray-900">
                                    {Array.isArray(v)
                                        ? <Chips items={v} catalog={catalog} tone={k === 'added' ? 'bg-green-50 text-green-700' : k === 'removed' ? 'bg-red-50 text-red-700' : undefined} />
                                        : valueText(v)}
                                </dd>
                            </div>
                        ))}
                    </dl>
                ) : (
                    <p className="text-sm text-gray-700 break-words">{valueText(data)}</p>
                )}
            </div>
            <div className="space-y-3 text-sm">
                <h4 className="text-xs font-medium uppercase tracking-wider text-gray-500">Source</h4>
                <div className="flex items-start gap-2 text-gray-700">
                    <Globe className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
                    <span>{row.ipAddress || <span className="text-gray-400">IP not recorded</span>}</span>
                </div>
                <div className="flex items-start gap-2 text-gray-700">
                    <Monitor className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
                    <span className="break-words">{row.userAgent || <span className="text-gray-400">Device not recorded</span>}</span>
                </div>
                <p className="text-xs text-gray-400">Event #{row.id} · {row.eventType}</p>
            </div>
        </div>
    );
}

const AuditLogTable = ({ rows, catalog, compact = false }) => {
    const { hasPermission } = useAuth();
    const [expanded, setExpanded] = useState(null);
    const canOpenClients = hasPermission('CLIENTS:READ');
    const cols = compact ? 4 : 6;

    return (
        <div className="overflow-x-auto">
            <table className="w-full min-w-[760px]">
                <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                        <th className="w-8" />
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                        {!compact && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Record</th>}
                        {!compact && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>}
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                    {rows.map((row) => {
                        const open = expanded === row.id;
                        const t = formatTime(row.timestamp);
                        const record = recordText(row);
                        return (
                            <React.Fragment key={row.id}>
                                <tr
                                    onClick={() => setExpanded(open ? null : row.id)}
                                    className={`cursor-pointer transition ${open ? 'bg-blue-50/40' : 'hover:bg-gray-50'}`}
                                >
                                    <td className="pl-4 text-gray-400">
                                        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap">
                                        <div className="text-sm text-gray-900">{t.date}</div>
                                        <div className="text-xs text-gray-500">{t.time}{t.relative && ` · ${t.relative}`}</div>
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="text-sm font-medium text-gray-900">{row.actorName || row.principal || 'System'}</div>
                                        {row.actorName && row.principal && <div className="text-xs text-gray-500">{row.principal}</div>}
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="text-sm text-gray-900">{row.eventLabel}</div>
                                        <span className={`mt-1 inline-block rounded-full px-2 py-0.5 text-xs font-medium ${categoryClass(row.category)}`}>
                                            {row.category}
                                        </span>
                                    </td>
                                    {!compact && (
                                        <td className="px-4 py-3 text-sm">
                                            {record ? (
                                                row.clientId && canOpenClients ? (
                                                    <Link
                                                        to={`/clients/${row.clientId}`}
                                                        onClick={(e) => e.stopPropagation()}
                                                        className="text-blue-600 hover:text-blue-800 hover:underline"
                                                    >
                                                        {record}
                                                    </Link>
                                                ) : <span className="text-gray-700">{record}</span>
                                            ) : <span className="text-gray-400">—</span>}
                                        </td>
                                    )}
                                    {!compact && (
                                        <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate" title={detailSummary(row.data)}>
                                            {detailSummary(row.data) || <span className="text-gray-400">—</span>}
                                        </td>
                                    )}
                                </tr>
                                {open && (
                                    <tr className="bg-gray-50/60">
                                        <td colSpan={cols}>
                                            <Details row={row} catalog={catalog} />
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

export default AuditLogTable;
